"use client"

import Image from "next/image"
import { cn } from "@/lib/utils"
import { m, LazyMotion, domMax } from "framer-motion"
import { useId } from "react"

export interface Tab {
  value: string
  label: React.ReactNode
  image?: string
  /**
   * Local extension on top of the DS API — used by pricing (MRI pill) and
   * locations (country flags) where a rendered node is preferred over an
   * image URL. Falls through the same 27px circular slot as `image`.
   */
  icon?: React.ReactNode
  /**
   * Grey out + block selection (e.g. pricing tabs for a product category that
   * isn't bookable yet in the current market — MRI / membership in a `checkup`
   * market). The button renders non-interactive.
   */
  disabled?: boolean
  /**
   * Trailing node rendered after the label (outside the truncating span, so it
   * never clips) — used by pricing to hang a "Bald verfügbar" pill on the
   * still-clickable coming-soon tabs.
   */
  badge?: React.ReactNode
  /** Keep the badge on phones. Badges hide below `sm` by default; set this
   *  when the badge carries the figure the tab exists for (explorer counts). */
  keepBadgeOnMobile?: boolean
}

export interface TabSwitcherProps {
  tabs: Tab[]
  activeTab: string
  onChange: (value: string) => void
  className?: string
}

export function TabSwitcher({
  tabs,
  activeTab,
  onChange,
  className,
}: TabSwitcherProps) {
  const layoutId = useId()
  const hasVisuals = tabs.some((tab) => tab.image || tab.icon)

  return (
    <LazyMotion features={domMax}>
      <div
        className={cn(
          "inline-flex max-w-full gap-1 rounded-full p-1",
          hasVisuals
            ? "bg-[rgba(228,228,228,0.6)] backdrop-blur-[30px]" // ds-color-ignore: translucent overlay
            : "bg-neutral-100",
          className
        )}
        role="tablist"
        aria-label="Tab switcher"
      >
        {tabs.map((tab) => {
          const isActive = tab.value === activeTab
          const isDisabled = tab.disabled
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-disabled={isDisabled}
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) onChange(tab.value)
              }}
              className={cn(
                "relative min-w-0 rounded-full",
                "transition-colors duration-[250ms]",
                hasVisuals
                  ? "flex h-10 items-center gap-2.5 py-[3px] pr-6 pl-2 max-sm:gap-1.5 max-sm:pr-3"
                  : "px-5 py-2",
                "type-title-sm",
                isDisabled
                  ? "cursor-not-allowed text-neutral-400 opacity-60"
                  : isActive
                    ? "cursor-pointer text-neutral-900"
                    : "cursor-pointer text-neutral-500 hover:text-neutral-700"
              )}
            >
              {isActive && (
                <m.span
                  layoutId={layoutId}
                  className="absolute inset-0 rounded-full bg-white"
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                />
              )}
              {tab.icon && (
                // The label gets the room on a phone (Health ri…).
                <span className="relative z-10 shrink-0 max-sm:hidden">
                  {tab.icon}
                </span>
              )}
              {tab.image && !tab.icon && (
                <Image
                  src={tab.image}
                  alt=""
                  width={27}
                  height={27}
                  className="relative z-10 size-[27px] shrink-0 rounded-full object-cover"
                />
              )}
              <span className="relative z-10 truncate">{tab.label}</span>
              {tab.badge && (
                <span
                  className={cn(
                    "relative z-10 shrink-0",
                    !tab.keepBadgeOnMobile && "max-sm:hidden"
                  )}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </LazyMotion>
  )
}
